import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '@/lib/CartContext';
import { ShoppingCart, Menu, X, Search, ChevronDown } from 'lucide-react';

const LOGO = "/logo.png";

export default function Header() {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const { itemCount } = useCart();
    const location = useLocation();

    const navLinks = [
        { href: '/', label: 'Inicio' },
        { href: '/productos', label: 'Productos' },
        { href: '/productos?category=juguetes-perros', label: 'Perros' },
        { href: '/productos?category=juguetes-gatos', label: 'Gatos' },
        { href: '/productos?category=accesorios', label: 'Accesorios' },
        { href: '/locales', label: '📍 Peluquerías', highlight: true },
    ];

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            window.location.href = `/productos?search=${encodeURIComponent(searchQuery.trim())}`;
            setSearchOpen(false);
            setSearchQuery('');
        }
    };

    return (
        <header className="sticky top-0 z-50 bg-white border-b border-cp-border-soft" data-testid="main-header">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-20 lg:h-24">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="header-logo">
                        <img src={LOGO} alt="CloudPet" className="h-[62px] lg:h-[70px] w-auto" />
                    </Link>

                    {/* Desktop Nav */}
                    <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
                        {navLinks.map(link => (
                            <Link
                                key={link.href}
                                to={link.href}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                    link.highlight
                                        ? location.pathname === '/locales'
                                            ? 'text-white bg-cp-secondary'
                                            : 'text-cp-secondary border border-cp-secondary/40 hover:bg-cp-secondary hover:text-white'
                                        : location.pathname === link.href
                                            ? 'text-cp-secondary bg-cp-secondary-soft'
                                            : 'text-cp-text-main hover:text-cp-secondary hover:bg-cp-bg-alt'
                                }`}
                            >
                                {link.label}
                            </Link>
                        ))}
                    </nav>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                        {/* Search toggle */}
                        <button
                            data-testid="search-toggle"
                            onClick={() => setSearchOpen(!searchOpen)}
                            className="p-2.5 rounded-xl text-cp-text-body hover:text-cp-secondary hover:bg-cp-bg-alt transition-colors"
                        >
                            <Search size={20} />
                        </button>

                        {/* Cart */}
                        <Link
                            to="/carrito"
                            data-testid="header-cart"
                            className="relative p-2.5 rounded-xl text-cp-text-body hover:text-cp-secondary hover:bg-cp-bg-alt transition-colors"
                        >
                            <ShoppingCart size={20} />
                            {itemCount > 0 && (
                                <span className="absolute -top-0.5 -right-0.5 bg-cp-secondary text-white text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full">
                                    {itemCount > 9 ? '9+' : itemCount}
                                </span>
                            )}
                        </Link>

                        {/* Mobile hamburger */}
                        <button
                            data-testid="mobile-menu-toggle"
                            onClick={() => setMobileOpen(!mobileOpen)}
                            className="lg:hidden p-2.5 rounded-xl text-cp-text-body hover:bg-cp-bg-alt transition-colors"
                        >
                            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
                        </button>
                    </div>
                </div>

                {/* Search bar */}
                {searchOpen && (
                    <form onSubmit={handleSearch} className="pb-4" data-testid="search-form">
                        <div className="relative">
                            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-cp-text-muted" />
                            <input
                                data-testid="search-input"
                                type="text"
                                placeholder="Buscar productos..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 bg-cp-bg-alt border border-cp-border rounded-xl text-sm text-cp-text-main placeholder:text-cp-text-muted focus:border-cp-primary"
                                autoFocus
                            />
                        </div>
                    </form>
                )}
            </div>

            {/* Mobile menu */}
            {mobileOpen && (
                <div className="lg:hidden bg-white border-t border-cp-border-soft" data-testid="mobile-menu">
                    <nav className="px-4 py-4 space-y-1">
                        {navLinks.map(link => (
                            <Link
                                key={link.href}
                                to={link.href}
                                onClick={() => setMobileOpen(false)}
                                className={`block px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                                    link.highlight
                                        ? 'text-cp-secondary font-bold'
                                        : location.pathname === link.href
                                            ? 'text-cp-secondary bg-cp-secondary-soft'
                                            : 'text-cp-text-main hover:bg-cp-bg-alt'
                                }`}
                            >
                                {link.label}
                            </Link>
                        ))}
                    </nav>
                </div>
            )}
        </header>
    );
}
